import React, { useState } from 'react';
import { Search, MapPin, Truck, CheckCircle, XCircle, Package, Sparkles, AlertCircle } from 'lucide-react';
import { useMyContext } from '../../../../context/MyContext';

import NavBar from '../../../../component/b2c/navBar/navBar';
import Sidebar from '../../../../component/b2c/sidebar/sidebar';
import { Base_Url, getpincode } from '../../../../config/config';
import { toast } from 'react-toastify';

const PincodeService = () => {
  const [pincode, setPincode] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const { isDarkMode, isToggleSidebar } = useMyContext();
  const [pincodeData, setPincodeData] = useState([]);
  const [showResults, setShowResults] = useState(false);

  const handleCheck = async () => {
    setIsChecking(true);
    setShowResults(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pincode}`;

    try {
      const response = await fetch(urlgetPincode, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data && data.status === "SUCCESS") {
        setPincodeData(data.data);
        setIsChecking(false);
        setShowResults(true);
      } else if (data && data.message === "Unauthorized access - Invalid token") {
        toast.error(data.message + " Please login Again");
        setIsChecking(false);
      } else if (data && data.status === "FAIL") {
        toast.error("Something Went Wrong Please Try Again With Another Pincode");
        setIsChecking(false);
      } else {
        toast.error("Failed to fetch city and state data");
        setIsChecking(false);
      }
    } catch (error) {
      console.error('Error fetching pincode data:', error);
      toast.error("Error fetching pincode data");
      setIsChecking(false);
    }
  };

  const hasResults = showResults && pincodeData && pincodeData.length > 0;
  const noResults = showResults && (!pincodeData || pincodeData.length === 0);

  return (
    <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-white'}`}>
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className={`fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20`}
            style={
              window.innerWidth < 768
                ? { top: "113px", height: "calc(100vh - 115px)", overflowY: 'auto' }
                : {}
            }>
            <Sidebar />
          </div>
        )}

        <div className='relative bg-gray-100 dark:shadow-custom-dark overflow-y-auto lg:overflow-hidden custom-scrollbar rounded-custom w-full mt-3 mb-3 mr-3 ml-4'>

          {/* Ambient background */}
          <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-custom">
            <div className="absolute -top-24 -left-24 w-72 h-72 bg-customPurple/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 -right-16 w-72 h-72 bg-blue-400/10 rounded-full blur-3xl"></div>
          </div>

          <div className="relative h-full lg:h-full flex flex-col p-4 md:p-6 dark:bg-darkmodeColor">

            {/* Compact header */}
            <div className="flex items-center gap-3 mb-4 md:mb-6 shrink-0 animate-fade-in">
              <div className="relative shrink-0">
                <div className="absolute -inset-1.5 bg-customPurple/20 rounded-2xl blur-md"></div>
                <div className="relative w-11 h-11 flex items-center justify-center rounded-2xl bg-gradient-to-br from-customPurple to-purple-600 shadow-lg">
                  <Package className="w-5 h-5 text-white" />
                </div>
              </div>
              <div>
                <h1 className="text-lg md:text-2xl font-black tracking-tight bg-gradient-to-r from-customPurple to-blue-500 bg-clip-text text-transparent">
                  Pincode Service Checker
                </h1>
                <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400">
                  Check delivery availability in your area instantly
                </p>
              </div>
            </div>

            {/* Split panel: search + results, fills remaining height */}
            <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-5 flex-1 min-h-0">

              {/* Left - Search Card */}
              <div className="bg-white dark:bg-darkmodeColor rounded-2xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden flex flex-col shrink-0">
                <div className="bg-gradient-to-r from-customPurple to-purple-600 px-5 py-4 flex items-center gap-3 shrink-0">
                  <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                    <MapPin className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-white leading-tight">Check Availability</h2>
                    <p className="text-[11px] text-purple-100">Enter your 6-digit pincode</p>
                  </div>
                </div>

                <div className="p-5 flex flex-col gap-5">
                  {/* Pincode input */}
                  <div>
                    <label htmlFor="pincode" className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">
                      Pincode
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                      <input
                        id="pincode"
                        type="text"
                        maxLength="6"
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="Enter 6-digit pincode"
                        value={pincode}
                        onChange={(e) => setPincode(e.target.value.replace(/\D/g, ''))}
                        className="w-full pl-10 pr-4 py-3 rounded-xl border-2 border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-white/5 text-sm font-medium text-gray-800 dark:text-white placeholder:text-gray-400 placeholder:font-normal focus:border-customPurple focus:ring-2 focus:ring-customPurple/20 focus:bg-white dark:focus:bg-white/10 outline-none transition-all duration-200"
                      />
                    </div>
                  </div>

                  {/* Button */}
                  <button
                    onClick={handleCheck}
                    disabled={isChecking || pincode.length !== 6}
                    className={`w-full py-3 rounded-xl text-sm font-semibold text-white transition-all flex items-center justify-center gap-2 ${
                      isChecking || pincode.length !== 6
                        ? 'bg-gray-300 dark:bg-gray-600 cursor-not-allowed'
                        : 'bg-gradient-to-r from-customPurple to-purple-600 hover:opacity-90 hover:shadow-lg hover:shadow-customPurple/30'
                    }`}
                  >
                    {isChecking ? (
                      <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                        Checking...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4" />
                        Check Availability
                      </>
                    )}
                  </button>

                  {/* Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                      <Truck className="w-4 h-4 text-customPurple mb-1" />
                      <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">Multiple Couriers</p>
                    </div>
                    <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-xl">
                      <Sparkles className="w-4 h-4 text-green-600 mb-1" />
                      <p className="text-[11px] font-semibold text-gray-700 dark:text-gray-200">Instant Check</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right - Results panel (always present, state-driven content) */}
              <div className="bg-white dark:bg-darkmodeColor rounded-2xl shadow-xl ring-1 ring-black/5 dark:ring-white/10 overflow-hidden flex flex-col min-h-[260px]">

                {hasResults && (
                  <>
                    <div className="bg-gradient-to-r from-customPurple to-purple-600 px-5 py-4 shrink-0">
                      <h3 className="text-sm font-bold text-white flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Service Available · {pincodeData.length} {pincodeData.length === 1 ? 'result' : 'results'}
                      </h3>
                    </div>
                    <div className="overflow-y-auto custom-scrollbar flex-1">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-purple-100 to-blue-100 dark:from-purple-900/30 dark:to-blue-900/30 sticky top-0">
                          <tr>
                            <th className="p-3 md:p-4 text-left text-[11px] md:text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              <div className="flex items-center"><MapPin className="w-3.5 h-3.5 mr-1.5 text-customPurple" />Pincode</div>
                            </th>
                            <th className="p-3 md:p-4 text-left text-[11px] md:text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">City</th>
                            <th className="p-3 md:p-4 text-left text-[11px] md:text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">State</th>
                            <th className="p-3 md:p-4 text-left text-[11px] md:text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                              <div className="flex items-center"><Truck className="w-3.5 h-3.5 mr-1.5 text-customPurple" />Partners</div>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                          {pincodeData.map((shipment, index) => {
                            const partners = [];
                            if (shipment.delhivery === "1") partners.push({ name: "Delhivery", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300" });
                            if (shipment.shiprocket === "1") partners.push({ name: "Shiprocket", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300" });
                            if (shipment.ecom === "1") partners.push({ name: "Ecom", color: "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300" });
                            if (shipment.ekart === "1") partners.push({ name: "Ekart", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300" });
                            if (shipment.xpressbees === "1") partners.push({ name: "XpressBees", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300" });

                            return (
                              <tr key={index} className="hover:bg-purple-50 dark:hover:bg-purple-900/10 transition-colors duration-200">
                                <td className="p-3 md:p-4 text-sm font-bold text-customPurple">{shipment.pincode}</td>
                                <td className="p-3 md:p-4 text-sm font-semibold text-gray-800 dark:text-gray-200">{shipment.city}</td>
                                <td className="p-3 md:p-4 text-sm text-gray-700 dark:text-gray-300">{shipment.state}</td>
                                <td className="p-3 md:p-4">
                                  {partners.length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5">
                                      {partners.map((partner, idx) => (
                                        <span key={idx} className={`px-2.5 py-1 rounded-full text-[11px] font-bold transition-transform duration-200 hover:scale-105 ${partner.color}`}>
                                          ✓ {partner.name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="flex items-center text-red-500 font-medium text-sm">
                                      <XCircle className="w-4 h-4 mr-1" />No Partner
                                    </span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}

                {noResults && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-8 animate-fade-in">
                    <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
                      <AlertCircle className="w-8 h-8 text-red-500" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-gray-800 dark:text-white mb-2">No Service Available</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300 max-w-sm">Sorry, we don't have delivery partners for this pincode yet.</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Please try another pincode or check back later.</p>
                  </div>
                )}

                {!showResults && (
                  <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
                    <div className="w-16 h-16 rounded-full bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center mb-4">
                      <MapPin className="w-8 h-8 text-customPurple/60" />
                    </div>
                    <h3 className="text-base md:text-lg font-semibold text-gray-500 dark:text-gray-400">Results will appear here</h3>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-xs">Enter a 6-digit pincode on the left and check availability.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in-right {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.6s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PincodeService;